// Implements "Beacons and bid capture" (Atlas ch.4 B10) - specifically
// `POST {origin}/v1/e`, the fire-and-forget render/playback beacon
// (confirmed live: `curl -D -` returns 204 with
// `access-control-allow-origin: *`, same origin B1/B2 already resolve).
// Deliberately independent of `src/api/`'s configureBackend() gate - that's
// a different, older contract requiring an explicit publisher opt-in;
// `/v1/e` needs nothing beyond the siteKey boot() already has, the same way
// B1/B2 do, so callers here never need a publisher to do anything extra.
//
// Never imports from `src/widgets/*` or `src/api/*` - callers (today, only
// `core/boot.js`'s renderUnit()) supply everything through the payload.

// `apiKey` in B10's payload is the SAME value as B1's own `siteKey` (its
// response literally echoes `siteKey: "pk_live_..."` in the same pk_live_
// format B10's examples use) - callers pass their siteKey as apiKey here,
// there is no separate credential to mint or fetch.
export function fireBeacon(origin, payload) {
  if (!origin || typeof fetch === 'undefined') return;
  try {
    fetch(`${origin}/v1/e`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ts: Date.now(), ...payload }),
      // Matches TrackingDispatcher's own convention for a beacon that might
      // legitimately fire right as the page navigates away (e.g. a click).
      keepalive: true,
    }).catch(() => {});
  } catch {
    // A synchronous fetch() throw (e.g. in an environment that validates the
    // URL eagerly) must never reach the caller - firing a beacon can never
    // break the page, matching every other "never throw" boundary here.
  }
}

const MRC_VIEWABILITY_RATIO = 0.5;
const MRC_DWELL_MS = 1000;

/**
 * A one-shot MRC-style viewability gate for the B10 "viewable"/"impression"
 * beacons - fires `onViewable()` exactly once, the first time `element` has
 * been >= 50% intersecting AND the tab itself visible, CONTINUOUSLY, for
 * `dwellMs` (default 1000 - Atlas ch.4 B10's own rule: "Impression fires on
 * VIEWABLE render - MRC: 50% of pixels for 1 s, 2 s for video" - callers
 * pass 2000 for video units, matching that explicit distinction).
 *
 * Any interruption (scrolled out of view, tab hidden) RESETS the dwell clock
 * to zero rather than pausing and resuming it from where it left off -
 * simpler to reason about and verify, and at least as strict as "the clock
 * pauses" (the same dwell language Atlas B11 uses for refresh eligibility).
 *
 * Self-disconnects once fired; if it never fires (page navigated away first)
 * there is nothing to explicitly tear down - the observer/listener go away
 * with the page itself, the same as every other unmanaged per-page resource
 * in a plain <script> embed. No-ops (never throws) in an environment without
 * IntersectionObserver.
 *
 * Returns a `stop()` function for an OWNER that outlives its element less
 * than the page does (a widget's destroy() - ADR-027's one-owner/one-cleanup
 * rule): it disconnects the observer, removes the listener and clears the
 * dwell timer, and `onViewable()` never fires afterward. Idempotent, and a
 * no-op once already fired. Callers that never stop it (boot()'s own
 * page-lifetime units) can keep ignoring the return value.
 */
export function observeViewability(element, onViewable, dwellMs = MRC_DWELL_MS) {
  if (typeof IntersectionObserver === 'undefined' || !element) return () => {};

  let visibleSince = null;
  let timer = null;
  let fired = false;
  let lastRatio = 0;

  const evaluate = () => {
    if (fired) return;
    const viewableNow = lastRatio >= MRC_VIEWABILITY_RATIO && !document.hidden;
    if (viewableNow) {
      if (visibleSince === null) {
        visibleSince = Date.now();
        clearTimeout(timer);
        timer = setTimeout(check, dwellMs);
      }
    } else {
      visibleSince = null;
      clearTimeout(timer);
    }
  };

  const check = () => {
    if (fired || visibleSince === null || Date.now() - visibleSince < dwellMs) {
      return;
    }
    fired = true;
    observer.disconnect();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    onViewable();
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      lastRatio = entry.intersectionRatio;
      evaluate();
    },
    { threshold: [0, MRC_VIEWABILITY_RATIO] },
  );
  observer.observe(element);

  const onVisibilityChange = () => evaluate();
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    if (fired) return;
    fired = true;
    clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

// Atlas ch.14: one auction's bids for one slot - winners AND losers, since
// losing bids are what tune floors and timeouts. Fire-and-forget like
// fireBeacon(): the route always answers 204 and a failure here must never
// reach the page. `keepalive` lets a batch sent as the reader leaves land.
export function sendBidBatch(origin, apiKey, batch) {
  if (!origin || !apiKey || !batch || typeof fetch === 'undefined') return;
  try {
    fetch(`${origin}/v1/bids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey, ...batch }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Same as fireBeacon(): a measurement failure is never the page's problem.
  }
}
